import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUsers, FaChartBar, FaMoneyBillWave, FaArrowLeft, FaCogs } from 'react-icons/fa';
import { getFirestore, doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import './Admin.css';

const Admin = () => {
  const [globalBankMoney, setGlobalBankMoney] = useState(null);
  const [globalBankHahumCoin, setGlobalBankHahumCoin] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [lastLogin, setLastLogin] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const { user, appCheckToken } = useFireAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        const db = getFirestore();
        const auth = getAuth();

        try {
          const globalBankRef = doc(db, 'bank', 'globalBank');
          const globalBankSnap = await getDoc(globalBankRef);
          if (globalBankSnap.exists()) {
            setGlobalBankMoney(globalBankSnap.data().money);
            setGlobalBankHahumCoin(globalBankSnap.data().hahumCoin);
          }
        } catch (error) {
          console.error("Error fetching global bank data:", error);
        }

        try {
          const usersRef = collection(db, 'users');
          const userSnapshot = await getDocs(usersRef);
          setTotalUsers(userSnapshot.size);
        } catch (error) {
          console.error("Error counting users:", error);
        }

        const currentUser = auth.currentUser;
        if (currentUser && currentUser.metadata.lastSignInTime) {
          setLastLogin(new Date(currentUser.metadata.lastSignInTime));
        }

        setActiveUsers("N/A");
      }
    };

    fetchData();
  }, [user]);

  const handleFunctionClick = async (url, successMessage) => {
    setIsProcessing(true);
    setFeedbackMessage('');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-firebase-appcheck': appCheckToken
        }
      });
      const result = await response.text();
      console.log(result);
      setFeedbackMessage(successMessage);
    } catch (error) {
      console.error('Error triggering function:', error);
      setFeedbackMessage('Error triggering function. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualStockAnalysis = () => {
    handleFunctionClick(
      'https://europe-west1-project-yob.cloudfunctions.net/manualStockAnalysis',
      'Stock analysis report generated successfully.'
    );
  };

  const handleManualRaffleDraw = () => {
    handleFunctionClick(
      'https://europe-west1-project-yob.cloudfunctions.net/manualRaffleDraw',
      'Raffle draw executed successfully.'
    );
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-section">
      <div className="admin-container">
        <div className="admin-header">
          <FaArrowLeft className="admin-back-arrow" onClick={() => navigate(-1)} />
          <h2 className="admin-title">Admin</h2>
        </div>
        
        <div className="admin-grid">
          <div className="admin-card user-management">
            <h3><FaUsers /> Management</h3>
            <ul>
              <li><Link to="/portal/admin/users">User Management</Link></li>
              <li><Link to="/portal/admin/bank">Bank of Hahum</Link></li>
              <li><Link to="/portal/admin/store">Item Admin</Link></li>
              <li><Link to="/portal/admin/raffle">Raffle Admin</Link></li>
              <li><Link to="/portal/admin/notices">Noticeboard</Link></li>
            </ul>
          </div>

          <div className="admin-card analytics">
            <h3><FaChartBar /> Analytics</h3>
            <p>Total Users: {totalUsers}</p>
            <p>Active Users: {activeUsers}</p>
            <p>Last Login: {lastLogin ? lastLogin.toLocaleString() : 'N/A'}</p>
          </div>

          <div className="admin-card financial">
            <h3><FaMoneyBillWave /> Financial Overview</h3>
            <p>Bank Money: {globalBankMoney !== null ? `💴 ${globalBankMoney.toFixed(2)}` : 'Loading...'}</p>
            <p>Bank HahumCoin: {globalBankHahumCoin !== null ? `🪙 ${globalBankHahumCoin.toFixed(2)}` : 'Loading...'}</p>
          </div>

          <div className="admin-card functions">
            <h3><FaCogs /> Functions</h3>
            <button 
              className="function-btn-action" 
              onClick={handleManualStockAnalysis}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Publish Market News'}
            </button>
            <button 
              className="function-btn-action" 
              onClick={handleManualRaffleDraw}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Run Raffle Draw'}
            </button>
            {feedbackMessage && <p className="feedback-message">{feedbackMessage}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelayRender(Admin, 300);
